import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock } from "lucide-react";
import { TIDS } from "@/lib/testIds";
import Seo from "@/components/Seo";
import { LOGIN_TITLE, LOGIN_DESCRIPTION } from "@/content/siteMeta";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center px-6 relative overflow-hidden">
      <Seo
        title={LOGIN_TITLE}
        description={LOGIN_DESCRIPTION}
        path="/login"
        noindex
      />
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <Link
        to="/"
        className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur-2xl p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark className="h-11 w-auto" />
            <div>
              <div className="font-display font-semibold text-lg leading-none">Dental Leads Lab</div>
              <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em] text-blue-400/80 mt-1">
                Admin Access
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl tracking-tight mb-2">Sign in</h1>
          <p className="text-white/55 text-sm mb-8">
            Access your leads dashboard.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs font-mono-accent uppercase tracking-[0.18em] text-white/60 mb-2 block">
                Email
              </Label>
              <Input
                data-testid={TIDS.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@dentalleadslab.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-mono-accent uppercase tracking-[0.18em] text-white/60 mb-2 block">
                Password
              </Label>
              <Input
                data-testid={TIDS.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                required
              />
            </div>
            <Button
              data-testid={TIDS.loginSubmit}
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-amber-400 text-[#070B14] hover:bg-amber-300 font-medium transition-colors active:scale-[0.98] disabled:opacity-60"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
